import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Eye, EyeOff } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

export default function SilverBannerTab() {
  const [bannerData, setBannerData] = useState({
    title: 'Silver Rates',
    subtitle: "Today's Silver Prices",
    rates: [],
    is_visible: true
  });
  const [newWeight, setNewWeight] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBannerData();
  }, []);

  const fetchBannerData = async () => {
    try {
      const res = await api.get('/silver-banner');
      setBannerData({
        title: res.data.title || 'Silver Rates',
        subtitle: res.data.subtitle || "Today's Silver Prices",
        rates: res.data.rates || [],
        is_visible: res.data.is_visible !== false
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddRate = () => {
    if (!newWeight || !newAmount) {
      toast.error('Please enter both weight and amount');
      return;
    }

    const weight = parseFloat(newWeight);
    const amount = parseFloat(newAmount);

    if (isNaN(weight) || isNaN(amount) || weight <= 0 || amount <= 0) {
      toast.error('Please enter valid positive numbers');
      return;
    }

    // Check if weight already exists
    if (bannerData.rates.some(r => r.weight === weight)) {
      toast.error('This weight already exists. Remove it first to update.');
      return;
    }

    setBannerData(prev => ({
      ...prev,
      rates: [...prev.rates, { weight, amount }].sort((a, b) => a.weight - b.weight)
    }));

    setNewWeight('');
    setNewAmount('');
    toast.success('Rate added. Click Save to persist changes.');
  };

  const handleRemoveRate = (index) => {
    setBannerData(prev => ({
      ...prev,
      rates: prev.rates.filter((_, i) => i !== index)
    }));
    toast.success('Rate removed. Click Save to persist changes.');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.post('/silver-banner', bannerData);
      toast.success('Silver banner saved successfully!');
      // Refetch to confirm save
      await fetchBannerData();
    } catch (error) {
      toast.error('Failed to save banner: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = () => {
    setBannerData(prev => ({
      ...prev,
      is_visible: !prev.is_visible
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3a5c]"></div>
      </div>
    );
  }

  return (
    <div data-testid="silver-banner-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c]">Silver Rate Banner</h2>
        <a 
          href="/silver-rates" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-[#1a3a5c] hover:underline flex items-center gap-1"
        >
          <Eye className="h-4 w-4" />
          View Public Page
        </a>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-6">
        {/* Banner Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Banner Title</label>
            <input
              type="text"
              value={bannerData.title}
              onChange={(e) => setBannerData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Silver Rates"
              data-testid="banner-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
            <input
              type="text"
              value={bannerData.subtitle}
              onChange={(e) => setBannerData(prev => ({ ...prev, subtitle: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Today's Silver Prices"
              data-testid="banner-subtitle-input"
            />
          </div>
        </div>

        {/* Visibility Toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleVisibility}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-colors ${
              bannerData.is_visible 
                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="visibility-toggle"
          >
            {bannerData.is_visible ? (
              <>
                <Eye className="h-4 w-4" />
                <span>Visible to Public</span>
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4" />
                <span>Hidden from Public</span>
              </>
            )}
          </button>
        </div>

        {/* Add New Rate */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold text-[#1a3a5c] mb-3">Add Weight & Amount</h3>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Weight (grams)</label>
              <input
                type="number"
                step="0.1"
                value={newWeight}
                onChange={(e) => setNewWeight(e.target.value)}
                className="w-32 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                placeholder="e.g., 10"
                data-testid="new-weight-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹)</label>
              <input
                type="number"
                step="0.01"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                className="w-40 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                placeholder="e.g., 850"
                data-testid="new-amount-input"
              />
            </div>
            <button
              onClick={handleAddRate}
              className="bg-[#C9A967] text-white px-4 py-2 rounded-sm hover:bg-[#b8954d] transition-colors flex items-center gap-2"
              data-testid="add-rate-btn"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>
        </div>

        {/* Rates List */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold text-[#1a3a5c] mb-3">Current Rates</h3>
          {bannerData.rates.length === 0 ? (
            <p className="text-gray-500 text-sm">No rates added yet. Add weights and amounts above.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 border-b">Weight (g)</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 border-b">Amount (₹)</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 border-b">Rate/g</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-700 border-b">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bannerData.rates.map((rate, index) => (
                    <tr key={`rate-${rate.weight}-${rate.amount}`} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-800 font-medium">{rate.weight}g</td>
                      <td className="px-4 py-3 text-gray-800">₹{rate.amount.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        ₹{(rate.amount / rate.weight).toFixed(2)}/g
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleRemoveRate(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                          data-testid={`remove-rate-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Preview */}
        {bannerData.rates.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-[#1a3a5c] mb-3">Preview</h3>
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-serif font-bold text-gray-800 mb-1">{bannerData.title}</h4>
              <p className="text-gray-600 text-sm mb-4">{bannerData.subtitle}</p>
              <div className="flex flex-wrap gap-4">
                {bannerData.rates.map((rate, index) => (
                  <div key={`preview-${rate.weight}-${rate.amount}`} className="bg-white px-4 py-3 rounded-lg shadow-sm">
                    <span className="text-lg font-bold text-[#1a3a5c]">{rate.weight}g</span>
                    <span className="text-gray-400 mx-2">→</span>
                    <span className="text-lg font-bold text-[#C9A967]">₹{rate.amount.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="border-t pt-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-6 py-2 rounded-sm transition-colors ${
              saving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-[#1a3a5c] hover:bg-[#1f4268] text-white'
            }`}
            data-testid="save-banner-btn"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Banner</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
